import { NgClass } from "@angular/common";
import {
  Component,
  computed,
  inject,
  input,
  ChangeDetectionStrategy,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { IconComponent } from "@claas/claas-ui-components";
import { TranslatePipe } from "@ngx-translate/core";
import { ConfigService } from "../../config/config.service";
import { FileSizePipe } from "../../pipes/file-size.pipe-pipe";
import { DocumentIdStorageService } from "../../services/document-id-storage.service";
import { FavoriteDocumentsService } from "../../services/favorite-documents.service";

@Component({
  selector: "claas-data-document-row",
  imports: [IconComponent, TranslatePipe, FileSizePipe, NgClass],
  templateUrl: "./data-document-row.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./data-document-row.scss",
})
export class DataDocumentRow {
  configService = inject(ConfigService);
  favoriteDocuments = inject(FavoriteDocumentsService);
  documentID = inject(DocumentIdStorageService);

  newBadge = input<boolean>(false);

  name = input<string>("");

  language = input<string>("");

  format = input<string>("");

  size = input<string>("");

  id = input<string>("");

  favoriteDocumentsList = toSignal(this.favoriteDocuments.includedDocumentIds$);
  isDocumentIDOn = toSignal(this.documentID.documentID$);

  isDocumentFavorite = computed(() =>
    this.favoriteDocumentsList()?.includes(this.id()),
  );
  isDocumentIDOnForCurrentDocument = computed(() => this.isDocumentIDOn());

  filename = computed(() => {
    const nameValue = this.name().trim();
    const formatValue = this.format().trim().toLowerCase();

    if (!nameValue && formatValue) {
      return `untitled.${formatValue}`;
    }

    return `${nameValue}${formatValue ? `.${formatValue}` : ""}`;
  });

  addDocumentToFavorites(id: string) {
    if (!this.favoriteDocumentsList()?.includes(id)) {
      this.favoriteDocuments.add(id);
    } else {
      this.favoriteDocuments.remove(id);
    }
  }

  /**
   * used to rename the file in any case (even though browser prefers name from path instead of download attribute name)
   * @param evt
   */
  async handleDownloadLinkClick(evt: MouseEvent) {
    const anchor = evt.currentTarget as HTMLAnchorElement;
    const { href } = anchor;

    if (href && anchor.getAttribute("download")) {
      evt.preventDefault();
      const outsideRes = await fetch(href);

      const blob = await outsideRes.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = anchor.getAttribute("download") as string;
      link.click();
    }
  }
}
